import { useReducer } from 'react';
import type { ReactNode } from 'react';
import type {
  EnglishComfortLevel,
  ConcernType,
} from '@/types/profile';
import {
  OnboardingContext,
  type OnboardingState,
  type QuickWinResult,
} from './useOnboardingContext';

type OnboardingAction =
  | { type: 'SET_NAME'; payload: string }
  | { type: 'SET_INTERVIEW_DATE'; payload: string | null }
  | { type: 'SET_ENGLISH_LEVEL'; payload: EnglishComfortLevel }
  | { type: 'SET_CONCERNS'; payload: ConcernType[] }
  | { type: 'TOGGLE_CONCERN'; payload: ConcernType }
  | { type: 'SET_STEP'; payload: number }
  | { type: 'SET_QUICK_WIN_RESULT'; payload: QuickWinResult }
  | { type: 'RESET' };

const initialState: OnboardingState = {
  full_name: '',
  interview_date: null,
  english_comfort_level: undefined,
  nervous_about: [],
  currentStep: 1,
  quickWinResult: null,
};

function onboardingReducer(
  state: OnboardingState,
  action: OnboardingAction
): OnboardingState {
  switch (action.type) {
    case 'SET_NAME':
      return { ...state, full_name: action.payload };
    case 'SET_INTERVIEW_DATE':
      return { ...state, interview_date: action.payload };
    case 'SET_ENGLISH_LEVEL':
      return { ...state, english_comfort_level: action.payload };
    case 'SET_CONCERNS':
      return { ...state, nervous_about: action.payload };
    case 'TOGGLE_CONCERN': {
      const concerns = state.nervous_about || [];
      const newConcerns = concerns.includes(action.payload)
        ? concerns.filter((c) => c !== action.payload)
        : [...concerns, action.payload];
      return { ...state, nervous_about: newConcerns };
    }
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_QUICK_WIN_RESULT':
      return { ...state, quickWinResult: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(onboardingReducer, initialState);

  const setName = (name: string) =>
    dispatch({ type: 'SET_NAME', payload: name });
  const setInterviewDate = (date: string | null) =>
    dispatch({ type: 'SET_INTERVIEW_DATE', payload: date });
  const setEnglishLevel = (level: EnglishComfortLevel) =>
    dispatch({ type: 'SET_ENGLISH_LEVEL', payload: level });
  const setConcerns = (concerns: ConcernType[]) =>
    dispatch({ type: 'SET_CONCERNS', payload: concerns });
  const toggleConcern = (concern: ConcernType) =>
    dispatch({ type: 'TOGGLE_CONCERN', payload: concern });
  const setStep = (step: number) =>
    dispatch({ type: 'SET_STEP', payload: step });
  const setQuickWinResult = (result: QuickWinResult) =>
    dispatch({ type: 'SET_QUICK_WIN_RESULT', payload: result });
  const reset = () => dispatch({ type: 'RESET' });

  return (
    <OnboardingContext.Provider
      value={{
        state,
        setName,
        setInterviewDate,
        setEnglishLevel,
        setConcerns,
        toggleConcern,
        setStep,
        setQuickWinResult,
        reset,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}
